import React from 'react';
import { fromIso } from '../../utils/businessDate';
import { getTenantToday } from '../../utils/dateUtils';
import styled from 'styled-components';
import { CalendarDays, CircleDollarSign, Database, Shield, UserRound } from 'lucide-react';
import { axiomTheme } from '../../styles/designTokens';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import { useBusinessDate } from '../../contexts/BusinessDateContext';

const Strip = styled.div`
  position: sticky;
  top: 64px;
  z-index: 40;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  background: rgba(2, 6, 23, 0.76);
  backdrop-filter: blur(16px);
`;

const Inner = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  min-height: 36px;
  padding: 0.35rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const Cluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const ContextItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.pill};
  background: rgba(15, 23, 42, 0.58);
  color: ${axiomTheme.color.text.muted};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1;
  padding: 0.36rem 0.55rem;
  text-transform: uppercase;

  svg {
    width: 13px;
    height: 13px;
    color: ${axiomTheme.color.tone.cyan};
  }
`;

const Value = styled.span`
  color: ${axiomTheme.color.text.primary};
`;

const BusinessDateItem = styled(ContextItem)<{ $closing?: boolean }>`
  border-color: ${p => p.$closing ? 'rgba(251, 191, 36, 0.35)' : axiomTheme.color.border.subtle};
  background: ${p => p.$closing ? 'rgba(251, 191, 36, 0.1)' : 'rgba(15, 23, 42, 0.58)'};

  svg {
    color: ${p => p.$closing ? axiomTheme.color.tone.amber : axiomTheme.color.tone.cyan};
  }
`;

const EnvironmentPill = styled(ContextItem)<{ $prod?: boolean }>`
  border-color: ${p => p.$prod ? 'rgba(52, 211, 153, 0.28)' : 'rgba(251, 191, 36, 0.28)'};

  svg {
    color: ${p => p.$prod ? axiomTheme.color.tone.emerald : axiomTheme.color.tone.amber};
  }
`;

const formatDate = (date: Date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

export const GlobalContextStrip: React.FC = () => {
  const { user } = useAuth();
  const { tenantName, isInstitutional, isRetail } = useTenantConfig();
  const { info } = useBusinessDate();
  const businessDate = fromIso(info?.businessDate || getTenantToday());
  const closing = !!info && info.status !== 'OPEN';
  const dateLabel = info?.status === 'EOD_RUNNING' ? 'Closing'
    : info?.status === 'EOD_FAILED' ? 'EOD stopped' : 'Business date';
  const appEnv = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production';
  const isProd = appEnv === 'production';

  const roleLens = user?.role || (isInstitutional ? 'PM' : isRetail ? 'Advisor' : 'User');
  const tenantLabel = tenantName && tenantName !== 'Default' ? tenantName : 'AxiomFlow';

  return (
    <Strip>
      <Inner>
        <Cluster>
          <ContextItem title="Current legal entity / tenant">
            <Database />
            Entity <Value>{tenantLabel}</Value>
          </ContextItem>
          <ContextItem title="Current operating role lens">
            <UserRound />
            Lens <Value>{roleLens}</Value>
          </ContextItem>
          <BusinessDateItem
            $closing={closing}
            data-testid="business-date-chip"
            title={closing
              ? 'End of day is in progress. New tickets default to the next business date.'
              : 'The date the book is open for. Screens and tickets default to it; it moves when end of day finishes.'}
          >
            <CalendarDays />
            {dateLabel} <Value>{formatDate(businessDate)}</Value>
          </BusinessDateItem>
        </Cluster>
        <Cluster>
          <ContextItem title="Position basis">
            <Shield />
            Basis <Value>Trade + Settle</Value>
          </ContextItem>
          <ContextItem title="Reporting currency">
            <CircleDollarSign />
            Currency <Value>USD</Value>
          </ContextItem>
          <EnvironmentPill $prod={isProd} title="Runtime environment">
            <Shield />
            Env <Value>{isProd ? 'Production' : appEnv}</Value>
          </EnvironmentPill>
        </Cluster>
      </Inner>
    </Strip>
  );
};

export default GlobalContextStrip;
